import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Text,
  Input,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  Checkbox,
} from '@chakra-ui/react';
import supabase from '../utils/supabaseUtils';

// Simple CSV parser (no quoted commas support)
function parseCSV(text) {
  const rows = text
    .trim()
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0);
  if (rows.length === 0) return [];
  const headers = rows[0].split(',').map((h) => h.trim());
  return rows.slice(1).map((row) => {
    const values = row.split(',');
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = (values[idx] || '').trim();
    });
    return obj;
  });
}

const csvHeaders = [
  'email',
  'firstName',
  'lastName',
  'phone',
  'athlete_first_name',
  'athlete_last_name',
  'team_name',
  'terms_accepted',
];

const AdminBulkSignup = () => {
  const { eventId } = useParams();
  const toast = useToast();

  const [participants, setParticipants] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [dryRun, setDryRun] = useState(true);

  const downloadTemplate = () => {
    const csvContent = `${csvHeaders.join(',')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'bulk_signup_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseCSV(text);
      if (parsed.length === 0) {
        toast({ title: 'No data found in CSV', status: 'warning' });
        return;
      }
      setParticipants(parsed);
    } catch (err) {
      console.error(err);
      toast({ title: 'Failed to read file', status: 'error' });
    }
  };

  const handleUpload = async () => {
    if (participants.length === 0) {
      toast({ title: 'No participants to upload', status: 'warning' });
      return;
    }
    setUploading(true);
    try {
      const { data, error } = await supabase.functions.invoke('bulk-event-signup', {
        body: {
          eventId,
          dryRun,
          participants: participants.map((p) => ({
            email: p.email,
            firstName: p.firstName,
            lastName: p.lastName,
            phone: p.phone,
            answers: {
              athlete_first_name: p.athlete_first_name,
              athlete_last_name: p.athlete_last_name,
              team_name: p.team_name,
              terms_accepted: (String(p.terms_accepted || '').toLowerCase() === 'true') || p.terms_accepted === '1',
            },
          })),
        },
      });
      if (error) throw error;
      setResults(data);
      toast({ title: 'Bulk signup completed', status: 'success' });
    } catch (err) {
      console.error(err);
      toast({ title: 'Bulk signup failed', description: err.message, status: 'error' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxW="6xl" py={10}>
      <VStack align="stretch" spacing={6}>
        <Heading size="lg">Bulk Signup for Event {eventId}</Heading>
        <Text>
          Download the CSV template, fill in participant details, and upload the file. Confirmation
          emails will NOT be sent.
        </Text>
        <Box>
          <Button colorScheme="blue" onClick={downloadTemplate} mr={4}>
            Download CSV Template
          </Button>
          <Input type="file" accept=".csv" onChange={handleFileChange} display="inline-block" width="auto" />
          <Checkbox ml={4} mt={{ base: 2, md: 0 }} isChecked={dryRun} onChange={(e)=>setDryRun(e.target.checked)}>
            Dry Run (no data saved)
          </Checkbox>
        </Box>

        {participants.length > 0 && (
          <>
            <Text fontWeight="bold">Preview ({participants.length} rows):</Text>
            <Box maxH="300px" overflow="auto" border="1px solid #eee" borderRadius="md">
              <Table size="sm">
                <Thead>
                  <Tr>
                    {csvHeaders.map((h) => (
                      <Th key={h}>{h}</Th>
                    ))}
                  </Tr>
                </Thead>
                <Tbody>
                  {participants.slice(0, 25).map((p, idx) => (
                    <Tr key={idx}>
                      {csvHeaders.map((h) => (
                        <Td key={h}>{p[h]}</Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Button
              colorScheme="green"
              onClick={handleUpload}
              isDisabled={uploading}
              leftIcon={uploading ? <Spinner size="xs" /> : undefined}
            >
              {uploading ? 'Uploading...' : 'Start Import'}
            </Button>
          </>
        )}

        {results && (
          <Box>
            <Heading size="md" mt={6} mb={2}>Results</Heading>
            {Array.isArray(results.results) ? (
              <Table size="sm">
                <Thead>
                  <Tr>
                    <Th>Email</Th>
                    <Th>Status</Th>
                    <Th>Message</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {results.results.map((r, idx) => (
                    <Tr key={idx}>
                      <Td>{r.email}</Td>
                      <Td>{r.status}</Td>
                      <Td>{r.message || '-'}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            ) : (
              <Text>No result data returned.</Text>
            )}
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default AdminBulkSignup; 